import {
    Component,
    Input,
    type OnInit,
    inject,
    ChangeDetectionStrategy,
} from "@angular/core";
import { Router } from "@angular/router";
import { DATA_QAA, dataQaaPrefix } from "@firm360/libs/observability/data-qaa";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { UrlUtils } from "../../../../shared/util/url-util";
import type { IUserAppNotification } from "../../model/user-app-notification.model";
import { UserAppNotificationService } from "../../service/user-app-notification.service";

interface IContent {
    before: string;
    linkText: string;
    link: string;
    after: string;
}

@Component({
    selector: "app-user-app-notification-detail-modal",
    hostDirectives: [DATA_QAA],
    templateUrl: "./user-app-notification-detail-modal.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class UserAppNotificationDetailModalComponent implements OnInit {
    protected readonly qaaPrefix = dataQaaPrefix(
        UserAppNotificationDetailModalComponent,
    );

    private readonly userAppNotificationService = inject(
        UserAppNotificationService,
    );
    private readonly activeModal = inject(NgbActiveModal);
    private readonly router = inject(Router);
    private readonly urlUtils = inject(UrlUtils);

    static readonly casuallyDismissible = true;
    @Input() protected notification: IUserAppNotification;
    protected content: IContent;

    ngOnInit(): void {
        if (!this.notification) {
            return;
        }

        if (!this.notification.readFlag) {
            this.userAppNotificationService
                .update({ ...this.notification, readFlag: true })
                .subscribe(
                    (notification) => (this.notification = notification),
                );
        }
        this.content = this.extractTextAndLink(this.notification.message);
    }

    softDelete(): void {
        this.userAppNotificationService
            .update({ ...this.notification, inactive: true })
            .subscribe(() => this.activeModal.close());
    }

    handleLinkClick(): void {
        if (!this.content.link) {
            return;
        }
        this.router.navigateByUrl(this.content.link).then(() => this.dismiss());
    }

    dismiss(): void {
        this.activeModal.dismiss();
    }

    private extractTextAndLink(input: string): IContent | null {
        if (!input) {
            return null;
        }
        const lines = input.split("\n");
        const linkIndex = lines.findIndex(
            (line) => line.includes("<") && line.includes(">"),
        );

        if (linkIndex === -1) {
            return null; // Link not found
        }

        const before = lines
            .slice(0, linkIndex - 1)
            .join("\n")
            .trim();
        const linkLine = lines[linkIndex].trim();
        const bracketPos = linkLine.indexOf("<");
        const linkText = (
            bracketPos === 0
                ? lines[linkIndex - 1]
                : linkLine.slice(0, bracketPos)
        ).trim();
        const linkMatch = linkLine.match(/<([^>]+)>/);

        if (!linkMatch) {
            return null; // Link format not as expected
        }

        const absoluteLink = linkMatch[1].trim();
        if (!this.urlUtils.urlRegex.test(absoluteLink)) {
            return null; // not a link
        }
        const splitLink = absoluteLink.split("#");
        if (splitLink.length < 2) {
            return null; // wrong relative address
        }
        const link = splitLink[1];
        const after = lines
            .slice(linkIndex + 1)
            .join("\n")
            .trim();

        return {
            before,
            linkText,
            link,
            after,
        };
    }
}
